import React, { useState, useRef } from "react";
import { X, ShoppingCart, Trash2 } from "lucide-react";

const ShoppingListView = ({
  items,
  members,
  onAdd,
  onCheck,
  onDelete,
  onClearChecked,
}) => {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [assignedMemberId, setAssignedMemberId] = useState(null);
  const inputRef = useRef(null);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({
      name: name.trim(),
      quantity: quantity.trim() || null,
      member_id: assignedMemberId || null,
    });
    setName("");
    setQuantity("");
    setAssignedMemberId(null);
    inputRef.current?.focus();
  };

  const unchecked = items.filter((i) => !i.checked);
  const checked = items.filter((i) => i.checked);

  const getMember = (id) => members.find((m) => m.id === id);

  const renderItem = (item) => {
    const member = item.member_id ? getMember(item.member_id) : null;
    return (
      <div
        key={item.id}
        className={`flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0 ${
          item.checked ? "opacity-50" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={item.checked}
          onChange={() => onCheck(item)}
          className="w-5 h-5 rounded cursor-pointer flex-shrink-0 accent-purple-600"
        />
        <div className="flex-1 min-w-0">
          <span
            className={`text-sm ${
              item.checked ? "line-through text-gray-400" : "text-gray-800"
            }`}
          >
            {item.name}
          </span>
          {item.quantity && (
            <span className="text-xs text-gray-400 ml-2">{item.quantity}</span>
          )}
        </div>
        {member && (
          <div
            className="w-5 h-5 rounded-full flex-shrink-0"
            style={{ backgroundColor: member.color }}
            title={member.name}
          />
        )}
        <button
          onClick={() => onDelete(item.id)}
          className="text-gray-300 hover:text-red-400 p-1 touch-manipulation flex-shrink-0"
          aria-label="Remove item"
        >
          <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Quick add form */}
      <form
        onSubmit={handleAdd}
        className="bg-white rounded-lg border border-gray-100 p-3 mb-4"
      >
        <div className="flex gap-2 mb-2">
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Add item..."
            className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            autoFocus
          />
          <input
            type="text"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Qty"
            className="w-16 border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation"
          >
            Add
          </button>
        </div>

        {/* Member assignment */}
        {members.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400">For:</span>
            {members.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() =>
                  setAssignedMemberId(assignedMemberId === m.id ? null : m.id)
                }
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border-2 transition-colors touch-manipulation ${
                  assignedMemberId === m.id
                    ? "text-white border-transparent"
                    : "border-gray-200 text-gray-600"
                }`}
                style={
                  assignedMemberId === m.id
                    ? { backgroundColor: m.color, borderColor: m.color }
                    : {}
                }
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: m.color }}
                />
                {m.name}
              </button>
            ))}
          </div>
        )}
      </form>

      {/* List */}
      {items.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <ShoppingCart size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-lg mb-1">List is empty!</p>
          <p className="text-sm">Add items above to get started.</p>
        </div>
      ) : (
        <>
          {unchecked.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 px-3 mb-4">
              {unchecked.map(renderItem)}
            </div>
          )}

          {checked.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  In Cart ({checked.length})
                </span>
                <button
                  onClick={onClearChecked}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 touch-manipulation"
                >
                  <Trash2 size={12} />
                  Clear all
                </button>
              </div>
              <div className="bg-white rounded-lg border border-gray-100 px-3">
                {checked.map(renderItem)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShoppingListView;
